import styled from 'styled-components'
import { textPrimary, error, primary, green } from 'styles/Colors'

const noticeColor = (variant?: 'error' | 'warning' | 'info'): string => {
  if (variant === 'error') return error
  if (variant === 'warning') return '#92400e'
  return primary
}

const noticeBackground = (variant?: 'error' | 'warning' | 'info'): string => {
  if (variant === 'error') return 'rgba(244, 62, 81, 0.08)'
  if (variant === 'warning') return 'rgba(235, 124, 12, 0.08)'
  return 'rgba(46, 76, 230, 0.06)'
}

const noticeBorder = (variant?: 'error' | 'warning' | 'info'): string => {
  if (variant === 'error') return 'rgba(244, 62, 81, 0.3)'
  if (variant === 'warning') return 'rgba(235, 124, 12, 0.3)'
  return 'rgba(46, 76, 230, 0.2)'
}

export const Area = styled.div`
  margin-top: 6.375rem;
  padding: 0 var(--dashboard-padding-x, 2.5rem) 3rem;
  background-color: white;
  min-height: 100%;
`

export const Heading = styled.h1`
  font-family: coresanscr65;
  font-size: 1.75rem;
  color: ${textPrimary};
`

export const SubHeading = styled.p`
  font-family: coresanscr45;
  font-size: 0.9375rem;
  color: ${textPrimary}99;
  margin-top: 0.25rem;
`

export const TabsHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-top: 1.5rem;
`

export const Tab = styled.button<{ active?: boolean }>`
  padding: 0.75rem 1.5rem;
  font-family: coresanscr55;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  color: ${({ active }) => (active ? '#EB7C0C' : textPrimary)};
  border-bottom-color: ${({ active }) => (active ? '#EB7C0C' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    color: #eb7c0c;
    background-color: rgba(235, 124, 12, 0.05);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(235, 124, 12, 0.2);
  }
`

export const TabBody = styled.div`
  padding: 1.5rem 0;
  min-height: 420px;
`

export const Panel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  background-color: white;
`

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.25rem;
`

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-family: coresanscr45;
  font-size: 0.75rem;
  color: ${textPrimary}99;
`

export const Select = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-family: coresanscr45;
  font-size: 0.875rem;
  color: ${textPrimary};
  background: white;
  min-width: 11rem;
`

export const DateInput = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.45rem 0.75rem;
  font-family: coresanscr45;
  font-size: 0.875rem;
  color: ${textPrimary};
`

export const PrimaryButton = styled.button`
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.6rem 1.25rem;
  font-family: coresanscr55;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const SecondaryButton = styled.button`
  background-color: white;
  color: ${textPrimary};
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-family: coresanscr55;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const TotalCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
`

export const TotalChip = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem 1.25rem;
  min-width: 10rem;

  span {
    display: block;
    font-family: coresanscr45;
    font-size: 0.75rem;
    color: ${textPrimary}99;
  }

  strong {
    font-family: coresanscr65;
    font-size: 1.25rem;
    color: ${green};
  }
`

export const Notice = styled.div<{ variant?: 'error' | 'warning' | 'info' }>`
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-family: coresanscr45;
  font-size: 0.875rem;
  margin: 0.75rem 0;
  color: ${({ variant }) => noticeColor(variant)};
  background-color: ${({ variant }) => noticeBackground(variant)};
  border: 1px solid ${({ variant }) => noticeBorder(variant)};
`

export const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  font-family: coresanscr55;
  font-size: 0.9375rem;
  color: ${textPrimary}99;
`

export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
`
